/**
 * The chronicle (BUILDLIST 62.15; decisions 47, 105, 46): a battle's key events, in order, kept with
 * its outcome beside the scar logs and the edit log. A battle nobody entered ran in the model, and its
 * chronicle is what you watch of it — the **recap** (47: "start with the recap"). A full battle's
 * chronicle comes from what the match recorded; a stale replay falls back to it (46).
 *
 * Events are small and plain: a time (seconds into the battle, or the round on the turn clock), a kind,
 * and who. Derived, never read by the sim.
 */
import type { GameState } from '../types';
import { PHYS_DT } from '../constants';
import type { BattleSetup, ChronicleEvent, FactionId, UnitId } from './types';
import { applyInput, step, type ModelBattle, type ModelInput } from './battleModel';

/**
 * A model battle's chronicle, by stepping it from `start` with `inputs` and diffing: each side's
 * first contact, every unit that falls (credited to the other side), each input as it lands, the end.
 * Deterministic (the model is), so the chronicle can be derived again rather than stored — it is
 * stored anyway, as the recap of a battle whose model may have changed by the time it is watched.
 */
export function chronicleFromModel(start: ModelBattle, inputs: Array<{ t: number; input: ModelInput }>): ChronicleEvent[] {
  const out: ChronicleEvent[] = [{ t: start.t, kind: 'start', text: `${start.sides[0].faction} ${start.sides[0].units.filter(u => u.alive).length} v ${start.sides[1].faction} ${start.sides[1].units.filter(u => u.alive).length}` }];
  const queue = [...inputs].sort((a, b) => a.t - b.t);
  let q = 0;
  let b = start;
  const contact = [false, false];
  const dt = start.params.stepSeconds ?? 1;
  const note = (prev: ModelBattle, next: ModelBattle) => {
    next.sides.forEach((side, i) => {
      if (!contact[i] && side.units.some(u => u.alive && u.engaged)) { contact[i] = true; out.push({ t: next.t, kind: 'contact', side: side.faction }); }
      const before = new Map(prev.sides[i].units.map(u => [u.unitId, u.alive]));
      for (const u of side.units) if (before.get(u.unitId) && !u.alive) out.push({ t: next.t, kind: 'down', side: side.faction, unitId: u.unitId, by: next.sides[1 - i].faction });
      if (!prev.sides[i].withdrawn && side.withdrawn) out.push({ t: next.t, kind: 'withdraw', side: side.faction });
    });
    if (!prev.ended && next.ended) out.push({ t: next.t, kind: 'end', side: next.ended.winner === null ? undefined : next.sides[next.ended.winner].faction, text: next.ended.reason });
  };
  let guard = 0;
  while (!b.ended && guard++ < 2_000_000) {
    while (q < queue.length && queue[q].t <= b.t + 1e-9) {
      const { input } = queue[q++];
      const side = b.sides[input.side].faction;
      if (input.kind === 'arrive') out.push({ t: b.t, kind: 'arrive', side, text: `${input.units.length} unit${input.units.length === 1 ? '' : 's'}` });
      else if (input.kind === 'fire') out.push({ t: b.t, kind: 'fire', side, text: `${input.shells} shell${input.shells === 1 ? '' : 's'} from outside` });
      else if (input.kind === 'policy') out.push({ t: b.t, kind: 'policy', side });
      const next = applyInput(b, input);
      note(b, next);
      b = next;
    }
    if (b.ended) break;
    const next = step(b, dt);
    note(b, next);
    b = next;
  }
  return out;
}

/**
 * A full battle's chronicle from what the match recorded: on the turn clock every killing shot
 * (`shotRecords`, by round), and the end. The action clock records no shots yet (62.12 brings its
 * decision log), so its chronicle is the start and the end with the standing.
 */
export function chronicleFromMatch(setup: BattleSetup, finalState: GameState, seconds?: number, winner?: FactionId | null, reason?: string | null): ChronicleEvent[] {
  const out: ChronicleEvent[] = [];
  const unitIdOf = new Map<number, UnitId>();
  setup.sides.slice(0, 2).forEach((side, t) => {
    const players = finalState.players.filter(p => p.teamIndex === t).sort((a, b) => a.id - b.id);
    side.units.forEach((u, k) => { if (players[k]) unitIdOf.set(players[k].id, u.unitId); });
  });
  const factionOf = (playerId: number): FactionId | undefined => {
    const p = finalState.players.find(x => x.id === playerId);
    return p ? setup.sides[p.teamIndex]?.faction : undefined;
  };
  const action = finalState.config.clock === 'action';
  out.push({ t: 0, kind: 'start', text: setup.sides.slice(0, 2).map(s => `${s.faction} ${s.units.length}`).join(' v ') });
  if (!action) {
    for (const r of finalState.shotRecords ?? []) {
      if (r.submunition || r.kills <= 0) continue;
      out.push({ t: r.turnNumber, kind: 'kill', side: factionOf(r.shooterId), unitId: unitIdOf.get(r.shooterId), text: r.kills > 1 ? `${r.kills} kills` : undefined });
    }
  }
  // X19: reserves arriving (each a wave's body stepping in for a fallen one)
  for (const a of finalState.reserveArrivals ?? []) out.push({ t: action ? a.tick * PHYS_DT : finalState.turnNumber, kind: 'arrive', side: setup.sides[a.teamIndex]?.faction, text: `reserve ${a.index + 1}${unitIdOf.has(a.playerId) ? ` steps in for ${unitIdOf.get(a.playerId)}` : ''}` });
  // X22: who got out, when
  for (const e of finalState.extractions ?? []) out.push({ t: action ? e.tick * PHYS_DT : finalState.turnNumber, kind: 'extracted', side: setup.sides[e.teamIndex]?.faction, unitId: unitIdOf.get(e.unitId) });
  const standing = setup.sides.slice(0, 2).map((side, t) => `${side.faction} ${finalState.players.filter(p => p.teamIndex === t && p.alive).length}${finalState.players.some(p => p.teamIndex === t && p.extracted) ? ` (+${finalState.players.filter(p => p.teamIndex === t && p.extracted).length} out)` : ''}`).join(' v ');
  out.push({ t: action ? (seconds ?? 0) : finalState.turnNumber, kind: 'end', side: winner ?? undefined, text: `${reason ?? 'ended'}; ${standing} standing` });
  return out;
}

/** A chronicle as lines a person reads, in the battle's own clock. */
export function recapLines(events: readonly ChronicleEvent[], clock: 'rounds' | 'action' | undefined, names?: (id: UnitId) => string): string[] {
  const when = (t: number) => (clock === 'action' ? (t >= 60 ? `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, '0')}` : `${Math.round(t)} s`) : `r${t}`);
  const unit = (id: UnitId | undefined) => (id === undefined ? '' : names ? names(id) : id);
  return events.map(e => {
    switch (e.kind) {
      case 'start': return `${when(e.t)} · battle joined: ${e.text ?? ''}`;
      case 'contact': return `${when(e.t)} · ${e.side} makes contact`;
      case 'down': return `${when(e.t)} · ${unit(e.unitId)} (${e.side}) falls${e.by ? ` to ${e.by}` : ''}`;
      case 'kill': return `${when(e.t)} · ${unit(e.unitId)} (${e.side}) scores${e.text ? ` ${e.text}` : ' a kill'}`;
      case 'arrive': return `${when(e.t)} · ${e.side} reinforced: ${e.text ?? ''}`;
      case 'withdraw': return `${when(e.t)} · ${e.side} withdraws`;
      case 'extracted': return `${when(e.t)} · ${unit(e.unitId)} (${e.side}) gets out`;
      case 'fire': return `${when(e.t)} · ${e.side} under fire: ${e.text ?? ''}`;
      case 'policy': return `${when(e.t)} · ${e.side} changes posture`;
      case 'end': return `${when(e.t)} · ${e.side ? `${e.side} wins` : 'no decision'} (${e.text ?? ''})`;
      default: return `${when(e.t)} · ${e.kind}`;
    }
  });
}
